import React, { useEffect, useCallback, useState, Fragment } from "react";
import api from "services/api";
import swal from "sweetalert";
import { Link } from "react-router-dom";
import moment from "moment";
import copy from "copy-to-clipboard";
import { toast, Flip } from "react-toastify";

import useQuery from "hooks/useQuery";
import useFilter from "hooks/useFilter";

import { Checkbox } from 'components/Checkbox';

import {
  Page,
  Form,
  Panel,
  Button,
  Icon,
  Loading,
  Pagination,
} from "components";

const PER_PAGE = 25;

const statusList = [
  { value: null, label: "Todos os Status" },
  { value: "created", label: "Não iniciado" },
  { value: "started", label: "Em andamento" },
  { value: "finished", label: "Finalizado" },
];

export default function List({ location, ...rest }) {
  const query = useQuery();

  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(1);
  const [interviews, setInterviews] = useState([]);
  const [search, setSearch] = useState(query.get("search") || "");
  const [status, setStatus] = useState(query.get("status") || null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([])

  useFilter({ key: "search", value: search, delay: 500, location });
  useFilter({ key: "status", value: status, delay: 0, location });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("search/interviews/page", {
        params: {
          page: query.get("page") || 1,
          per: PER_PAGE,
          filter: search,
          interview_status: status,
        },
      });
      const { data, meta } = response.data;

      setTotal(meta.total_count);
      setInterviews(
        data.map((interview) => ({
          ...interview,
          attributes: {
            ...interview.attributes,
            created_at: moment(interview.attributes.created_at).format(
              "DD/MM/YYYY [às] HH:mm"
            ),
            updated_at: moment(interview.attributes.updated_at).format(
              "DD/MM/YYYY [às] HH:mm"
            ),
            calculated_at: moment(interview.attributes.calculated_at).format(
              "DD/MM/YYYY [às] HH:mm"
            ),
          },
        }))
      );
    } catch (err) {
      swal(
        "",
        "Ops, houve algum de errado, não foi possível carregar as entrevistas, tente novamente.",
        "error"
      );
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  }, [query.get("page"), search, status]);

  const handleDelete = useCallback(async (id) => {
    const confirm = await swal({
      title: "Deseja remover?",
      text: "Tem certeza de que deseja remover esta entrevista?",
      type: "info",
      buttons: {
        cancel: "Não",
        confirm: {
          text: "Sim",
        },
      },
    });

    if (confirm) {
      try {
        await api.delete(`search/interviews/${id}`);

        swal("Succeso", "Entrevista removido com sucesso!", "success");
        await loadData();
      } catch (err) {
        swal("Ops, Falha", "Não foi possível remover a Entrevista!", "error");
      }
    }
  });

  const handleBulkDelete = useCallback(async () => {
    const confirm = await swal({
      title: "Deseja remover selecionados?",
      text: "Todas as entrevistas selecionadas serão removidas e não será mais possível recuperar os dados dela.",
      type: "info",
      buttons: {
        cancel: "Não",
        confirm: {
          text: "Sim",
        },
      },
    });

    if (confirm) {
      setIsDeleting(true);

      try {
        await Promise.all(
          selectedItems.map(async (interviewId) => {
            await api.delete(`search/interviews/${interviewId}`)
          })
        )

        swal('Sucesso', 'Entrevistas selecionadas removidas com sucesso!', 'success');
        setSelectedItems([])
        await loadData();
      } catch (err) {
        swal('Ops, falha', 'Não foi possível remover todas as entrevistas', 'error');
      } finally {
        setIsDeleting(false)
      }
    }
  });

  const handleSendEmail = useCallback(async (id) => {
    try {
      await api.get(`search/interviews/${id}/resendmail`);
      swal("Succeso", "E-mail enviado com sucesso!", "success");
    } catch (err) {
      swal("Ops, Falha", "Não foi possível enviar o e-mail!", "error");
    }
  }, []);

  const handleCopyLink = useCallback((interview) => {
    const { origin } = document.location;

    copy(`${origin}/#/magiclink/${interview.attributes.magichash}`);
    toast(`Magiclink de ${interview.attributes.person.name} copiado.`, {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      transition: Flip,
    });
  }, [])

  function toggleInterviewSelectAll() {
    if (interviews.length === selectedItems.length) {
      setSelectedItems([]);
      return;
    }

    const interviewIds = interviews.map(interview => interview.id)
    setSelectedItems(interviewIds);
  }

  function selectItem(interviewId) {
    if (selectedItems.includes(interviewId)) {
      setSelectedItems(selectedItems.filter(interview => interview !== interviewId))
      return;
    }

    setSelectedItems([...selectedItems, interviewId])
  }

  useEffect(() => {
    loadData();
  }, [window.location.hash]);

  return (
    <Page>
      <div className="row align-items-center">
        <div className="col-6">
          <Page.Title>Entrevistas</Page.Title>
        </div>

        <div className="col-6 text-right">
          {!!selectedItems.length && (
            <button type="button" className="btn btn-danger m-r-10" disabled={isDeleting} onClick={handleBulkDelete}>
              { isDeleting ? <Loading type="btn" size="small" text="Procesando ação em massa..." /> : 'Remover selecionados'}
            </button>
          )}

          <Link to="/admin/interviews/create" className="btn btn-success">
            Adicionar Nova
          </Link>

          <Link to="/admin/interviews/bulk" className="btn btn-inverse m-l-10">
            Importar em Massa
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-3 m-b-10">
          <Form.Input
            placeholder="Buscar por Participante, Curso ou Turma"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-3 m-b-10">
          <Form.Select
            placeholder="Status"
            options={statusList}
            value={statusList.find((option) => option.value === status)}
            onChange={(option) => setStatus(option.value)}
          />
        </div>
      </div>

      <Panel>
        {!interviews.length && loading ? (
          <Loading type="page" size="x-large" />
        ) : (
          <table className="table table-stripped">
            <thead>
              <tr>
                <th>
                  <Checkbox
                    checked={!!(interviews.length === selectedItems.length)}
                    onChange={toggleInterviewSelectAll}
                  />
                </th>
                <th>Nome</th>
                <th>Início</th>
                <th>Atualização</th>
                <th>Relatório</th>
                <th>Curso</th>
                <th>Jornada</th>
                <th>Turma</th>
                <th>Facilitador(a)/Coach (Email)</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>

            <tbody>
              <Fragment>
                {loading && <Loading type="table" size="x-large" />}

                {interviews.map((interview) => (
                  <tr key={String(interview.id)}>
                    <td>
                      <Checkbox
                        checked={!!selectedItems.includes(interview.id)}
                        onChange={() => selectItem(interview.id)}
                      />
                    </td>
                    <td>{interview.attributes.person.name}</td>
                    <td>{interview.attributes.created_at}</td>
                    <td>{interview.attributes.updated_at}</td>
                    <td>{interview.attributes.calculated_at}</td>
                    <td>{interview.attributes.course}</td>
                    <td>{interview.attributes.interviewable_type_name}</td>
                    <td>{interview.attributes.course_class}</td>
                    <td>{interview.attributes.coach_email}</td>
                    <td>
                      {interview.attributes.finished ? (
                        <span className="badge badge-success">Finalizado</span>
                      ) : interview.attributes.created_at !==
                        interview.attributes.updated_at ? (
                        <span className="badge badge-info">Em andamento</span>
                      ) : (
                        <span className="badge badge-danger">Não iniciado</span>
                      )}
                    </td>
                    <td className="text-right" width="280">
                      <Button
                        className="btn btn-inverse btn-sm m-r-5"
                        onClick={() => handleSendEmail(interview.id)}
                        title="Enviar e-mail"
                      >
                        <Icon name="envelope" />
                      </Button>

                      <Button
                        className="btn btn-inverse btn-sm m-r-5"
                        onClick={() => handleCopyLink(interview)}
                        title="Copiar Magiclink"
                      >
                        <Icon name="clipboard" />
                      </Button>

                      <Link
                        to={`/admin/interviews/${interview.id}/edit`}
                        className="btn btn-inverse btn-sm m-r-5"
                      >
                        <Icon name="pencil-alt" />
                      </Link>

                      <Button
                        className="btn btn-inverse btn-sm m-r-5"
                        onClick={() => handleDelete(interview.id)}
                      >
                        <Icon name="trash" />
                      </Button>
                      {interview.attributes.calculated && (
                        <a
                          className="btn btn-inverse btn-sm m-r-5"
                          href={`${process.env.REACT_APP_API_URL}reports/pdf/generate/${interview.id}`}
                          title="Download do Relatório"
                        >
                          <Icon name="download" />
                        </a>
                      )}

                      {interview.attributes.interviewable_type ===
                        "Search::Journey" &&
                        interview.attributes.calculated && (
                          <a
                            className="btn btn-inverse btn-sm m-r-5"
                            href={`${process.env.REACT_APP_API_URL}reports/pdf/generate/summary/${interview.id}`}
                            title="Download do Resumo"
                          >
                            <Icon name="file" />
                          </a>
                        )}
                    </td>
                  </tr>
                ))}
              </Fragment>
            </tbody>
          </table>
        )}
      </Panel>

      <Pagination
        className="m-t-20"
        total={total}
        per={PER_PAGE}
        location={location}
        {...rest}
      />
    </Page>
  );
}

// import React, { Component, Fragment } from 'react';
// import { toast, Flip } from 'react-toastify';
// import { debounce } from 'lodash';
// import { Link } from 'react-router-dom';
// import queryString from 'query-string';
// import swal from 'sweetalert';
// import copy from 'copy-to-clipboard';

// import { connect } from 'react-redux';
// import { bindActionCreators } from 'redux';
// import { Creators as InterviewsActions, Selectors as InterviewsSelectors } from 'store/ducks/interviews';

// import { Page, Panel, Button, Icon, Loading, Pagination, Form } from 'components';

// class List extends Component {
//   state = {
//     query: '',
//   };

//   constructor(props) {
//     super(props);

//     this.handleSearch = debounce(props.getInterviewsRequest, 300);
//   }

//   componentDidMount() {
//     const params = queryString.parse(this.props.location.search);
//     this.props.getInterviewsRequest(params.page);
//   }

//   componentDidUpdate(oldProps) {
//     const { location } = this.props;

//     if (queryString.parse(oldProps.location.search).page !== queryString.parse(location.search).page) {
//       this.props.getInterviewsRequest(
//         queryString.parse(location.search).page,
//         queryString.parse(location.search).filter,
//       );
//     }
//   }

//   handleChangeQuery = e => {
//     const { value } = e.target;

//     this.setState({ query: value });
//     this.handleSearch(1, value);
//   };

//   handleDelete = async id => {
//     const confirm = await swal({
//       title: 'Deseja remover?',
//       text: 'Tem certeza de que deseja remover esta entrevista?',
//       type: 'info',
//       buttons: {
//         cancel: 'Não',
//         confirm: {
//           text: 'Sim',
//         },
//       },
//     });

//     if (confirm) {
//       this.props.deleteInterviewRequest(id);
//     }
//   };

//   handleSendEmail = (e, id) => {
//     const { sendEmailRequest } = this.props;

//     sendEmailRequest(id);
//   };

//   handleCopyLink = interview => {
//     const { origin } = document.location;

//     copy(`${origin}/#/magiclink/${interview.attributes.magichash}`);
//     toast(`Magiclink de ${interview.attributes.name} copiado.`, {
//       position: 'bottom-right',
//       autoClose: 2000,
//       hideProgressBar: true,
//       closeOnClick: true,
//       pauseOnHover: false,
//       draggable: false,
//       transition: Flip,
//     });
//   };

//   render() {
//     const { interviews } = this.props;
//     const { query } = this.state;

//     return (
//       <Page>
//         <div className="row align-items-center">
//           <div className="col-6">
//             <Page.Title>Entrevistas</Page.Title>
//           </div>

//           <div className="col-6 text-right">
//             <Link to="/admin/interviews/create" className="btn btn-success">
//               Adicionar Nova
//             </Link>

//             <Link to="/admin/interviews/bulk" className="btn btn-inverse m-l-10">
//               Importar em Massa
//             </Link>
//           </div>
//         </div>

//         <div className="row">
//           <div className="col-12 col-md-6 col-lg-3 m-b-10">
//             <Form onSubmit={e => e.preventDefault()}>
//               <Form.Input
//                 name="query"
//                 placeholder="Buscar por Participante, Curso ou Turma"
//                 value={query}
//                 onChange={this.handleChangeQuery}
//               />
//             </Form>
//           </div>
//         </div>

//         <Panel>
//           {!interviews.data.length && interviews.loading ? (
//             <Loading type="page" size="x-large" />
//           ) : (
//             <Fragment>
//               {interviews.loading && <Loading type="table" size="x-large" />}

//               <table className="table table-stripped">
//                 <thead>
//                   <tr>
//                     <th>Nome</th>
//                     <th>Início</th>
//                     <th>Atualização</th>
//                     <th>Relatório</th>
//                     <th>Curso</th>
//                     <th>Jornada</th>
//                     <th>Turma</th>
//                     <th>Status</th>
//                     <th />
//                   </tr>
//                 </thead>

//                 <tbody>
//                   {interviews.data.map(interview => (
//                     <tr key={String(interview.id)}>
//                       <td>{interview.attributes.name}</td>
//                       <td>{interview.attributes.created_at}</td>
//                       <td>{interview.attributes.updated_at}</td>
//                       <td>{interview.attributes.calculated_at}</td>
//                       <td>{interview.attributes.course}</td>
//                       <td>{interview.attributes.interviewable_type_name}</td>
//                       <td>{interview.attributes.course_class}</td>
//                       <td>
//                         {interview.attributes.finished ? (
//                           <span className="badge badge-success">Finalizado</span>
//                         ) : interview.attributes.created_at !== interview.attributes.updated_at ?
//                         (
//                           <span className="badge badge-info">Em andamento</span>
//                         ) :(
//                           <span className="badge badge-danger">Não iniciado</span>
//                         )}
//                       </td>
//                       <td className="text-right" width="280">
//                         <Button
//                           className="btn btn-inverse btn-sm m-r-5"
//                           onClick={e => this.handleSendEmail(e, interview.id)}
//                           title="Enviar e-mail"
//                         >
//                           <Icon name="envelope" />
//                         </Button>

//                         <Button
//                           className="btn btn-inverse btn-sm m-r-5"
//                           onClick={() => this.handleCopyLink(interview)}
//                           title="Copiar Magiclink"
//                         >
//                           <Icon name="clipboard" />
//                         </Button>

//                         <Link to={`/admin/interviews/${interview.id}/edit`} className="btn btn-inverse btn-sm m-r-5">
//                           <Icon name="pencil" />
//                         </Link>

//                         <Button
//                           className="btn btn-inverse btn-sm m-r-5"
//                           onClick={() => this.handleDelete(interview.id)}
//                         >
//                           <Icon name="trash" />
//                         </Button>
//                         {interview.attributes.calculated && (
//                           <a
//                             className="btn btn-inverse btn-sm m-r-5"
//                             href={`${process.env.REACT_APP_API_URL}reports/pdf/generate/${interview.id}`}
//                             title="Download do Relatório"
//                           >
//                             <Icon name="download" />
//                           </a>
//                         )}

//                         {interview.attributes.interviewable_type === 'Search::Journey' &&
//                           interview.attributes.calculated && (
//                             <a
//                               className="btn btn-inverse btn-sm m-r-5"
//                               href={`${process.env.REACT_APP_API_URL}reports/pdf/generate/summary/${interview.id}`}
//                               title="Download do Resumo"
//                             >
//                               <Icon name="file" />
//                             </a>
//                           )}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </Fragment>
//           )}
//         </Panel>

//         <Pagination
//           className="m-t-20"
//           filter={query}
//           current={interviews.page}
//           total={interviews.total}
//           {...this.props}
//         />
//       </Page>
//     );
//   }
// }

// const mapState = state => ({
//   interviews: {
//     ...state.interviews,
//     data: InterviewsSelectors.list(state.interviews.data),
//   },
// });

// const mapActions = dispatch => bindActionCreators(InterviewsActions, dispatch);

// export default connect(
//   mapState,
//   mapActions,
// )(List);



// WEBPACK FOOTER //
// ./src/pages/Admin/Interviews/List/index.js